// WHY (#2103): colored display-only 3-state cargo status pill; isIssued
// branches first (overrides the stored status); mirrors RequestStatusPill but
// cargo-local (3 tones only, no shared abstraction).

import { type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { CARGO_STATUS_LABELS, type CargoArticleWire } from '@magimp/shared';

type Tone = 'info' | 'error' | 'success';

const STYLES: Record<Tone, CSSProperties> = {
  info: { background: 'var(--status-info-bg)', color: 'var(--status-info-fg)' },
  error: { background: 'var(--status-error-bg)', color: 'var(--status-error-fg)' },
  success: { background: 'var(--status-success-bg)', color: 'var(--status-success-fg)' },
};

export function CargoStatusPill({
  row,
  size = 'sm',
}: {
  row: CargoArticleWire;
  size?: 'sm' | 'md';
}) {
  const { t } = useTranslation('cargo');
  let tone: Tone;
  let label: string;
  if (row.isIssued) {
    tone = 'success';
    label = t('status.issued');
  } else if (row.status === 'arrived_rf') {
    tone = 'error';
    label = CARGO_STATUS_LABELS.arrived_rf;
  } else {
    tone = 'info';
    label = CARGO_STATUS_LABELS.in_transit;
  }
  const arrived = !row.isIssued && row.status === 'arrived_rf';
  const h = size === 'sm' ? 20 : 22;
  const px = size === 'sm' ? 7 : 9;
  // WHY (#2191): arrived_rf label overflows the 88px column; replace only the
  // first space so the label splits into two lines; pre-line renders \n as a
  // hard line-break; the trailing word stays on line 2 without further wrapping.
  const display = arrived ? label.replace(' ', '\n') : label;
  const style: CSSProperties = arrived
    ? {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        minHeight: h,
        paddingBlock: 2,
        paddingInline: px,
        borderRadius: 'var(--r-1)',
        fontSize: size === 'sm' ? 11 : 12,
        fontWeight: 500,
        lineHeight: 1.1,
        whiteSpace: 'pre-line' as const,
        ...STYLES[tone],
      }
    : {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        height: h,
        paddingInline: px,
        borderRadius: 'var(--r-1)',
        fontSize: size === 'sm' ? 11 : 12,
        fontWeight: 500,
        whiteSpace: 'nowrap' as const,
        ...STYLES[tone],
      };
  return (
    <span style={style}>
      <span
        style={{
          width: 5,
          height: 5,
          borderRadius: '50%',
          background: 'currentColor',
          flexShrink: 0,
        }}
      />
      {display}
    </span>
  );
}
